<template>
  <div class="page-main">
    <div class="page-top">
      <div class="flex flex-y-center">
        <span class="title">仓库平面图</span>
        <span class="c-gray m-l-10">拖动色块移动，拖右下角调整大小；用 ＋/－ 按钮整仓等比缩放；可开启占用热力图。</span>
      </div>
      <div class="flex flex-y-center">
        <el-button size="mini" :type="heatmap ? 'warning' : ''" @click="toggleHeatmap">
          {{ heatmap ? '关闭热力图' : '占用热力图' }}
        </el-button>
        <el-button-group class="m-l-10" v-show="heatmap">
          <el-button size="mini" :type="heatMode === 'qty' ? 'primary' : ''" @click="setHeatMode('qty')">按库存量</el-button>
          <el-button size="mini" :type="heatMode === 'kinds' ? 'primary' : ''" @click="setHeatMode('kinds')">按物料种类</el-button>
          <el-button size="mini" :type="heatMode === 'density' ? 'primary' : ''" @click="setHeatMode('density')">按占用密度</el-button>
        </el-button-group>
        <el-button-group class="m-l-10 m-r-10">
          <el-button size="mini" @click="zoomOut">－</el-button>
          <el-button size="mini" @click="zoomReset">{{ Math.round(zoom * 100) }}%</el-button>
          <el-button size="mini" @click="zoomIn">＋</el-button>
        </el-button-group>
        <span class="legend m-r-15" v-show="!heatmap">
          <span v-for="t in typeLegend" :key="t.type" class="legend-item">
            <i class="legend-dot" :style="{ background: t.color }"></i>{{ t.name }}
          </span>
        </span>
        <span class="legend m-r-15" v-show="heatmap">
          <span class="legend-item"><i class="legend-dot heat-low"></i>低</span>
          <span class="legend-item"><i class="legend-dot heat-mid"></i>中</span>
          <span class="legend-item"><i class="legend-dot heat-high"></i>高</span>
          <span class="legend-item c-gray">峰值 {{ round(maxHeat) }}{{ heatUnit }}</span>
        </span>
        <el-button size="mini" @click="autoLayout">全部重排</el-button>
        <el-button size="mini" @click="resetFilter">刷新/补位</el-button>
        <el-button type="success" size="mini" :loading="saving" @click="saveLayout">保存布局</el-button>
      </div>
    </div>

    <div class="content">
      <!-- 画布 -->
      <div class="canvas-wrap" :style="canvasWrapStyle">
        <div class="canvas-sizer" :style="sizerStyle">
          <div class="canvas" :style="canvasStyle">
          <div
            v-for="wh in warehouseList"
            :key="'wh-' + wh.id"
            class="wh-block"
            :class="{ active: selected && selected.type === 'wh' && selected.id === wh.id }"
            :style="whStyle(wh.id)"
          >
            <div class="wh-head" @mousedown="startMove('wh', wh.id, $event)">
              <span class="wh-name">{{ wh.warehouse_name }}</span>
              <span class="wh-code">{{ wh.warehouse_code }}</span>
            </div>
            <div class="wh-body">
              <div
                v-for="loc in locationsOf(wh.id)"
                :key="'loc-' + loc.id"
                class="loc-box"
                :class="{ active: selected && selected.type === 'loc' && selected.id === loc.id, empty: !qtyOf(loc.id) }"
                :style="locStyle(loc.id)"
                @mousedown="startMove('loc', loc.id, $event)"
              >
                <div class="loc-name" :title="loc.location_name">{{ loc.location_name }}</div>
                <div class="loc-code">{{ loc.location_code }}</div>
                <div class="loc-qty">{{ qtyText(loc.id) }}</div>
                <div class="resize-handle loc-resize" @mousedown.stop="startResize('loc', loc.id, $event)"></div>
              </div>
            </div>
            <div class="resize-handle wh-resize" @mousedown.stop="startResize('wh', wh.id, $event)"></div>
          </div>

          <div v-if="!warehouseList.length" class="canvas-empty">暂无仓库数据，请先到「仓库」页面创建。</div>
          </div>
        </div>
      </div>

      <!-- 右侧明细 -->
      <div class="detail-panel">
        <template v-if="selected && selected.type === 'loc'">
          <div class="detail-title">{{ selectedLoc.location_name }}</div>
          <div class="detail-sub">编码：{{ selectedLoc.location_code }}</div>
          <div class="detail-sub">类型：{{ typeText(selectedLoc.location_type) }}</div>
          <div class="detail-sub">所属仓库：{{ whName(selectedLoc.warehouse_id) }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count">当前库存：{{ selectedRows.length }} 种物料，合计 {{ qtyText(selectedLoc.id) }}</div>
          <div class="detail-list">
            <div v-for="(row, i) in selectedRows" :key="i" class="detail-row">
              <div class="dr-name">{{ row.material_name }}</div>
              <div class="dr-info">
                <span class="c-gray">{{ row.category }} {{ row.spec }}</span>
                <span class="dr-qty">{{ row.qty }}{{ row.unit }}</span>
              </div>
            </div>
            <div v-if="!selectedRows.length" class="c-gray p-10">该库位当前无库存。</div>
          </div>
        </template>
        <template v-else-if="selected && selected.type === 'wh'">
          <div class="detail-title">{{ selectedWh.warehouse_name }}</div>
          <div class="detail-sub">编码：{{ selectedWh.warehouse_code }}</div>
          <div class="detail-sub">类型：{{ whTypeText(selectedWh.warehouse_type) }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count">库位数：{{ locationsOf(selectedWh.id).length }} 个</div>
        </template>
        <template v-else>
          <div class="c-gray p-10">点击左侧仓库或库位色块查看详情。</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// 库位类型 -> 颜色
const TYPE_COLOR = { 1: '#409EFF', 2: '#67C23A', 3: '#E6A23C', 4: '#F56C6C', 5: '#909399' }
const TYPE_NAME = { 1: '货架位', 2: '地堆', 3: '暂存区', 4: '退货区', 5: '待检区' }
const GRID = 10 // 对齐网格(px)

export default {
  data() {
    return {
      loading: false,
      saving: false,
      warehouseList: [],
      locationList: [],
      inventoryMap: {}, // location_id -> { qty, rows:[] }
      // 布局几何
      whGeo: {}, // wid -> {x,y,w,h}
      locGeo: {}, // lid -> {x,y,w,h}
      canvas: { w: 1600, h: 1000 },
      wrapH: 400,
      // 拖拽状态
      drag: null,
      selected: null,
      // 视图缩放(整仓等比)与占用热力图
      zoom: 1,
      heatmap: false,
      heatMode: 'qty', // qty=按库存量, kinds=按物料种类数
      typeLegend: Object.keys(TYPE_NAME).map((k) => ({ type: k, name: TYPE_NAME[k], color: TYPE_COLOR[k] }))
    }
  },
  computed: {
    selectedLoc() {
      return this.selected && this.selected.type === 'loc' ? this.locationList.find((l) => String(l.id) === String(this.selected.id)) : null
    },
    selectedWh() {
      return this.selected && this.selected.type === 'wh' ? this.warehouseList.find((w) => String(w.id) === String(this.selected.id)) : null
    },
    selectedRows() {
      if (!this.selectedLoc) return []
      const info = this.inventoryMap[this.selectedLoc.id]
      return info ? info.rows : []
    },
    canvasWrapStyle() {
      return { height: this.wrapH + 'px' }
    },
    sizerStyle() {
      return { width: this.canvas.w * this.zoom + 'px', height: this.canvas.h * this.zoom + 'px' }
    },
    canvasStyle() {
      return {
        width: this.canvas.w + 'px',
        height: this.canvas.h + 'px',
        transform: 'scale(' + this.zoom + ')',
        transformOrigin: '0 0'
      }
    },
    maxHeat() {
      let m = 0
      this.locationList.forEach((l) => {
        const v = this.metricOf(l.id, this.inventoryMap[l.id])
        if (v > m) m = v
      })
      return m
    },
    heatUnit() {
      return this.heatMode === 'kinds' ? ' 种' : this.heatMode === 'density' ? ' 件/面积' : ''
    }
  },
  watch: {
    zoom() {
      this.saveView()
    },
    heatmap() {
      this.saveView()
    },
    heatMode() {
      this.saveView()
    }
  },
  mounted() {
    this.loadView()
    this.init()
    this.calcWrapH()
    window.addEventListener('mousemove', this.onMove)
    window.addEventListener('mouseup', this.onUp)
    window.addEventListener('resize', this.calcWrapH)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMove)
    window.removeEventListener('mouseup', this.onUp)
    window.removeEventListener('resize', this.calcWrapH)
  },
  methods: {
    // 画布高度按自身到视口顶部的实际距离动态计算，正好填满到底部，避免外层再出现一条全局竖向滚动条
    calcWrapH() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.canvas-wrap')
        if (!el) return
        const top = el.getBoundingClientRect().top
        this.wrapH = Math.max(240, window.innerHeight - top - 16)
      })
    },
    async init() {
      this.loading = true
      try {
        await Promise.all([this.getWarehouses(), this.getLocations(), this.getInventory()])
        await this.getLayout()
      } finally {
        this.loading = false
      }
    },
    getWarehouses() {
      return this.$api({
        method: 'post',
        url: '/admin/gcvip/newWarehouse/list',
        data: { currentPage: 1, pageSize: this.$constant.pageMax, sortData: 'id asc' }
      }).then((res) => {
        if (res.status === 200 && res.data.page) this.warehouseList = res.data.page.list || []
      })
    },
    getLocations() {
      return this.$api({
        method: 'post',
        url: '/admin/gcvip/warehouseLocation/list',
        data: { currentPage: 1, pageSize: this.$constant.pageMax, deleted: false, sortData: 'id asc' }
      }).then((res) => {
        if (res.status === 200 && res.data.page) this.locationList = res.data.page.list || []
      })
    },
    getInventory() {
      return this.$api({
        method: 'post',
        url: '/admin/gcvip/inventory/stocklist',
        data: { currentPage: 1, pageSize: this.$constant.pageMax, filterData: {}, sortData: { updated_at: 'desc' } }
      })
        .then((res) => {
          const list = (res.data && res.data.page && res.data.page.list) || []
          const map = {}
          list.forEach((row) => {
            const id = row.location_id
            if (id === null || id === undefined) return
            if (!map[id]) map[id] = { qty: 0, rows: [] }
            map[id].qty = this.$util.add ? this.$util.add(map[id].qty, row.qty || 0) : Number(map[id].qty) + Number(row.qty || 0)
            map[id].rows.push(row)
          })
          this.inventoryMap = map
        })
        .catch(() => {})
    },
    getLayout() {
      return this.$api({ method: 'get', url: '/admin/gcvip/warehouseLayout/find' })
        .then((res) => {
          const payload = res.data && res.data.data
          if (payload && payload.layout && payload.layout.warehouses && payload.layout.warehouses.length) {
            this.canvas.w = payload.canvas_w || 1600
            this.canvas.h = payload.canvas_h || 1000
            const whGeo = {}
            const locGeo = {}
            ;(payload.layout.warehouses || []).forEach((w) => {
              whGeo[w.warehouseId] = { x: w.x, y: w.y, w: w.w, h: w.h }
            })
            ;(payload.layout.locations || []).forEach((l) => {
              locGeo[l.locationId] = { x: l.x, y: l.y, w: l.w, h: l.h }
            })
            this.whGeo = whGeo
            this.locGeo = locGeo
            // 为新增但未布局的仓库/库位补自动排布
            this.ensureAllPlaced()
          } else {
            this.autoLayout()
          }
        })
        .catch(() => this.autoLayout())
    },

    // ---- 排布 ----
    autoLayout() {
      const gap = 24
      const off = 24
      const CW = 460
      const CH = 320
      const cols = Math.max(1, Math.ceil(Math.sqrt(this.warehouseList.length)))
      const whGeo = {}
      this.warehouseList.forEach((wh, i) => {
        const c = i % cols
        const r = Math.floor(i / cols)
        whGeo[wh.id] = { x: off + c * (CW + gap), y: off + r * (CH + gap), w: CW, h: CH }
      })
      this.whGeo = whGeo
      const locGeo = {}
      this.warehouseList.forEach((wh) => {
        const locs = this.locationList.filter((l) => String(l.warehouse_id) === String(wh.id))
        const box = this.gridInWarehouse(locs.length, whGeo[wh.id].w)
        locs.forEach((l, idx) => {
          locGeo[l.id] = {
            x: box.ox + (idx % box.perRow) * (box.cw + box.gap),
            y: box.oy + Math.floor(idx / box.perRow) * (box.ch + box.gap),
            w: box.cw,
            h: box.ch
          }
        })
      })
      this.locGeo = locGeo
      this.fitCanvas()
    },
    // 计算某仓库内库位网格参数
    gridInWarehouse(count, whW) {
      const pad = 10
      const gap = 12
      const head = 28
      const bodyW = Math.max(120, whW - pad * 2)
      const perRow = Math.max(1, Math.floor((bodyW + gap) / (140 + gap)))
      const cw = Math.max(120, Math.floor((bodyW - gap * (perRow - 1)) / perRow))
      const ch = 88
      return { ox: pad, oy: head + pad, gap, cw, ch, perRow }
    },
    // 清理已删除仓库/库位遗留的布局，并为新增未布局实体补位置（避让已有色块）
    ensureAllPlaced() {
      this.purgeStale()
      this.warehouseList.forEach((wh, i) => {
        if (!this.whGeo[wh.id]) {
          const last = this.warehouseList[i - 1]
          const prev = last ? this.whGeo[last.id] : null
          const x = prev ? prev.x + prev.w + 24 : 24
          const y = prev ? prev.y : 24
          const nx = x + 460 > this.canvas.w ? 24 : x
          const ny = x + 460 > this.canvas.w ? (prev ? prev.y + prev.h + 24 : 24) : y
          this.$set(this.whGeo, wh.id, { x: nx, y: ny, w: 460, h: 320 })
        }
      })
      this.warehouseList.forEach((wh) => {
        const geo = this.whGeo[wh.id]
        const box = this.gridInWarehouse(1, geo.w)
        const all = this.locationsOf(wh.id)
        const placed = all.filter((l) => this.locGeo[l.id]).map((l) => this.rect(this.locGeo[l.id]))
        const missing = all.filter((l) => !this.locGeo[l.id])
        missing.forEach((l) => {
          let idx = 0
          let r
          do {
            const col = idx % box.perRow
            const row = Math.floor(idx / box.perRow)
            r = { x: box.ox + col * (box.cw + box.gap), y: box.oy + row * (box.ch + box.gap), w: box.cw, h: box.ch }
            idx++
          } while (idx < 300 && placed.some((p) => this.rectIntersect(p, r)))
          this.$set(this.locGeo, l.id, r)
          placed.push(r)
        })
      })
      this.fitCanvas()
    },
    purgeStale() {
      const wids = this.warehouseList.map((w) => String(w.id))
      Object.keys(this.whGeo).forEach((id) => {
        if (wids.indexOf(String(id)) === -1) this.$delete(this.whGeo, id)
      })
      const lids = this.locationList.map((l) => String(l.id))
      Object.keys(this.locGeo).forEach((id) => {
        if (lids.indexOf(String(id)) === -1) this.$delete(this.locGeo, id)
      })
    },
    rect(g) {
      return { x: g.x, y: g.y, w: g.w, h: g.h }
    },
    rectIntersect(a, b) {
      return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y)
    },
    fitCanvas() {
      let maxX = 1200
      let maxY = 800
      Object.keys(this.whGeo).forEach((id) => {
        const g = this.whGeo[id]
        maxX = Math.max(maxX, g.x + g.w + 40)
        maxY = Math.max(maxY, g.y + g.h + 40)
      })
      this.canvas.w = maxX
      this.canvas.h = maxY
    },

    // ---- 拖拽/缩放 ----
    startMove(type, id, e) {
      e.preventDefault()
      this.selected = { type, id }
      const geo = type === 'wh' ? this.whGeo[id] : this.locGeo[id]
      if (!geo) return
      this.drag = {
        mode: 'move',
        type,
        id,
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
        orig: { x: geo.x, y: geo.y }
      }
    },
    startResize(type, id, e) {
      e.preventDefault()
      this.selected = { type, id }
      const geo = type === 'wh' ? this.whGeo[id] : this.locGeo[id]
      if (!geo) return
      this.drag = {
        mode: 'resize',
        type,
        id,
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
        orig: { w: geo.w, h: geo.h }
      }
    },
    onMove(e) {
      if (!this.drag) return
      // 屏幕像素换算为逻辑像素：除以缩放倍率，保证缩放后拖拽/缩放仍然跟手
      const dx = (e.clientX - this.drag.startX) / this.zoom
      const dy = (e.clientY - this.drag.startY) / this.zoom
      if (Math.abs(e.clientX - this.drag.startX) > 2 || Math.abs(e.clientY - this.drag.startY) > 2) this.drag.moved = true
      const { mode, type, id, orig } = this.drag
      if (type === 'wh') {
        const g = this.whGeo[id]
        if (mode === 'move') {
          // 仓库块不受当前画布尺寸限制：向下/向右可自由移动，画布实时扩展
          g.x = this.snap(Math.max(0, orig.x + dx))
          g.y = this.snap(Math.max(0, orig.y + dy))
        } else {
          g.w = this.snap(Math.max(240, orig.w + dx))
          g.h = this.snap(Math.max(160, orig.h + dy))
        }
        this.fitCanvas()
      } else {
        const g = this.locGeo[id]
        const wid = this.locWhId(id)
        const wg = this.whGeo[wid]
        const bodyW = wg ? wg.w : 460
        const bodyH = wg ? wg.h - 28 : 292
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, Math.min(orig.x + dx, bodyW - g.w)))
          g.y = this.snap(Math.max(0, Math.min(orig.y + dy, bodyH - g.h)))
        } else {
          g.w = this.snap(Math.max(80, Math.min(orig.w + dx, bodyW - g.x)))
          g.h = this.snap(Math.max(50, Math.min(orig.h + dy, bodyH - g.y)))
        }
      }
    },
    onUp() {
      if (this.drag && this.drag.type === 'wh') this.fitCanvas()
      this.drag = null
    },
    snap(v) {
      return Math.round(v / GRID) * GRID
    },

    // ---- 保存 ----
    saveLayout() {
      this.saving = true
      const warehouses = this.warehouseList
        .filter((w) => this.whGeo[w.id])
        .map((w) => {
          const g = this.whGeo[w.id]
          return { warehouseId: w.id, x: g.x, y: g.y, w: g.w, h: g.h }
        })
      const locations = this.locationList
        .filter((l) => this.locGeo[l.id])
        .map((l) => {
          const g = this.locGeo[l.id]
          return { locationId: l.id, warehouseId: l.warehouse_id, x: g.x, y: g.y, w: g.w, h: g.h }
        })
      this.$api({
        method: 'post',
        url: '/admin/gcvip/warehouseLayout/save',
        data: { layout: { warehouses, locations }, canvas_w: this.canvas.w, canvas_h: this.canvas.h }
      })
        .then((res) => {
          if (res.data.state !== 'fail') this.$message.success('布局已保存')
        })
        .catch(() => {})
        .then(() => {
          this.saving = false
        })
    },
    resetFilter() {
      this.selected = null
      this.init()
    },

    // ---- 辅助 ----
    locationsOf(wid) {
      return this.locationList.filter((l) => String(l.warehouse_id) === String(wid))
    },
    locWhId(lid) {
      const l = this.locationList.find((x) => String(x.id) === String(lid))
      return l ? l.warehouse_id : null
    },
    whStyle(wid) {
      const g = this.whGeo[wid] || { x: 0, y: 0, w: 460, h: 320 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    locStyle(lid) {
      const g = this.locGeo[lid] || { x: 10, y: 38, w: 140, h: 88 }
      const loc = this.locationList.find((l) => String(l.id) === String(lid))
      const color = loc ? TYPE_COLOR[Number(loc.location_type)] || '#909399' : '#909399'
      const style = { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px', borderLeftColor: color }
      const bg = this.heatColor(lid)
      if (bg) style.background = bg
      return style
    },
    // 占用热力图：按库位库存量占峰值的比例，绿(低)→红(高)渐变着色
    heatColor(lid) {
      if (!this.heatmap) return null
      const info = this.inventoryMap[lid]
      if (!info) return null
      const v = this.metricOf(lid, info)
      if (!v) return null
      const ratio = Math.min(1, v / (this.maxHeat || 1))
      const hue = 120 * (1 - ratio)
      const light = 82 - ratio * 24
      return 'hsl(' + Math.round(hue) + ',68%,' + Math.round(light) + '%)'
    },
    // 热力指标口径：qty=库存量, kinds=物料种类数, density=库存量/色块面积(每1000px²)
    metricOf(lid, info) {
      if (this.heatMode === 'kinds') return info && info.rows ? info.rows.length : 0
      const qty = info ? Number(info.qty) || 0 : 0
      if (this.heatMode === 'density') {
        const g = this.locGeo[lid]
        if (!g) return 0
        const area = (g.w * g.h) / 1000
        return area > 0 ? qty / area : 0
      }
      return qty
    },
    toggleHeatmap() {
      this.heatmap = !this.heatmap
    },
    setHeatMode(m) {
      this.heatMode = m
    },
    saveView() {
      try {
        localStorage.setItem('wh_layout_view', JSON.stringify({ zoom: this.zoom, heatmap: this.heatmap, heatMode: this.heatMode }))
      } catch (e) {
        /* ignore */
      }
    },
    loadView() {
      try {
        const s = localStorage.getItem('wh_layout_view')
        if (!s) return
        const o = JSON.parse(s) || {}
        if (o.zoom) this.zoom = o.zoom
        if (typeof o.heatmap === 'boolean') this.heatmap = o.heatmap
        if (o.heatMode) this.heatMode = o.heatMode
      } catch (e) {
        /* ignore */
      }
    },
    setZoom(z) {
      this.zoom = Math.max(0.3, Math.min(2, Math.round(z * 100) / 100))
    },
    zoomIn() {
      this.setZoom(this.zoom + 0.1)
    },
    zoomOut() {
      this.setZoom(this.zoom - 0.1)
    },
    zoomReset() {
      this.zoom = 1
    },
    qtyOf(lid) {
      const info = this.inventoryMap[lid]
      return info ? info.qty : 0
    },
    qtyText(lid) {
      const info = this.inventoryMap[lid]
      if (!info) return '空闲'
      return info.rows.length + '种 / ' + this.round(info.qty)
    },
    round(n) {
      const v = Number(n) || 0
      return v % 1 === 0 ? v : v.toFixed(2)
    },
    whName(id) {
      const w = this.warehouseList.find((x) => String(x.id) === String(id))
      return w ? w.warehouse_name : ''
    },
    typeText(val) {
      return TYPE_NAME[Number(val)] || ''
    },
    whTypeText(val) {
      const map = { 1: '普通仓', 2: '退货仓', 3: '虚拟仓', 4: '中转仓' }
      return map[Number(val)] || ''
    }
  }
}
</script>

<style lang="less" scoped>
.page-main {
  .page-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .title {
      font-size: 15px;
      font-weight: bold;
    }
    .legend-item {
      margin-right: 10px;
      font-size: 12px;
      .legend-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 4px;
        vertical-align: middle;
      }
      .legend-dot.heat-low {
        background: hsl(120, 68%, 82%);
      }
      .legend-dot.heat-mid {
        background: hsl(60, 68%, 70%);
      }
      .legend-dot.heat-high {
        background: hsl(0, 68%, 58%);
      }
    }
  }
  .content {
    display: flex;
    .canvas-wrap {
      flex: 1;
      min-width: 0;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #909399;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background-color: #f7f8fa;
      background-image: linear-gradient(#e8eaef 1px, transparent 1px), linear-gradient(90deg, #e8eaef 1px, transparent 1px);
      background-size: 20px 20px;
      .canvas-sizer {
        position: relative;
      }
      .canvas {
        position: absolute;
        top: 0;
        left: 0;
        transform-origin: 0 0;
        .canvas-empty {
          position: absolute;
          top: 40px;
          left: 40px;
          color: #909399;
        }
      }
    }
    .detail-panel {
      width: 260px;
      margin-left: 10px;
      padding: 10px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background: #fff;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      .detail-title {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 6px;
      }
      .detail-sub {
        font-size: 12px;
        color: #606266;
        line-height: 20px;
      }
      .detail-divider {
        border-top: 1px dashed #dcdfe6;
        margin: 8px 0;
      }
      .detail-count {
        font-size: 12px;
        margin-bottom: 6px;
      }
      .detail-list {
        .detail-row {
          padding: 6px;
          border-bottom: 1px solid #f0f0f0;
          .dr-name {
            font-size: 13px;
          }
          .dr-info {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            .dr-qty {
              color: #e6a23c;
            }
          }
        }
      }
    }
  }
}

.wh-block {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #c8ccd4;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  &.active {
    border-color: #396bf6;
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.25);
  }
  .wh-head {
    height: 28px;
    line-height: 28px;
    padding: 0 8px;
    background: #2f333b;
    color: #fff;
    border-radius: 6px 6px 0 0;
    cursor: move;
    white-space: nowrap;
    overflow: hidden;
    .wh-name {
      font-size: 13px;
    }
    .wh-code {
      float: right;
      font-size: 11px;
      color: #b7bcc7;
    }
  }
  .wh-body {
    position: absolute;
    top: 28px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
}

.loc-box {
  position: absolute;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-left: 4px solid #909399;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: move;
  overflow: hidden;
  font-size: 12px;
  &.active {
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.35);
    border-color: #396bf6;
  }
  &.empty {
    opacity: 0.75;
    background: #fafafa;
  }
  .loc-name {
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .loc-code {
    color: #909399;
    font-size: 11px;
  }
  .loc-qty {
    color: #e6a23c;
    font-size: 11px;
  }
}

.resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  right: 0;
  bottom: 0;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 40%, #396bf6 40%, #396bf6 55%, transparent 55%, transparent 70%, #396bf6 70%, #396bf6 85%, transparent 85%);
}
.wh-resize {
  z-index: 3;
}
.loc-resize {
  z-index: 2;
}
</style>
